import { useState, useRef } from "react";
import { RotateCcw } from "lucide-react";
import {
  polyolEquivalentWeight,
  isocyanateEquivalentWeight,
  ncoOhRatio,
  isocyanateRequirement,
  puMixRatio,
  waterNcoDemand,
  multiPolyolBlend,
  fmt,
} from "../../lib/calc";
import { trackEvent } from "../../lib/api";

const Field = ({ label, unit, value, onChange, testid, placeholder, step = "any" }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-brand-muted">
      {label} {unit && <span className="normal-case text-brand-muted/70">({unit})</span>}
    </span>
    <input
      type="number"
      inputMode="decimal"
      step={step}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-12 w-full border border-brand-line bg-white px-3 font-mono text-lg text-brand-ink outline-none focus:ring-2 focus:ring-brand-blue"
    />
  </label>
);

const ResultRow = ({ label, value, unit, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`flex items-center justify-between px-4 py-3 ${
      highlight ? "bg-brand-blue/8 border-l-2 border-brand-blue" : "border-b border-brand-line last:border-0"
    }`}
  >
    <span className="text-sm text-brand-muted">{label}</span>
    <span className={`font-mono text-lg ${highlight ? "font-semibold text-brand-blue" : "text-brand-ink"}`}>
      {value} <span className="text-xs text-brand-muted">{unit}</span>
    </span>
  </div>
);

const ResetBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="calc-reset"
    className="inline-flex items-center gap-1.5 border border-brand-line px-4 py-2 text-sm font-medium text-brand-muted transition-colors hover:border-brand-red hover:text-brand-red"
  >
    <RotateCcw className="h-4 w-4" /> Reset
  </button>
);

// Fire the calculator_usage analytics event once per mount, not per keystroke.
const useOnceTracker = (slug) => {
  const done = useRef(false);
  return () => {
    if (!done.current) {
      done.current = true;
      trackEvent("calculator_usage", slug);
    }
  };
};

const Shell = ({ children, footer }) => (
  <div className="border border-brand-line bg-brand-bg">
    <div className="border-b border-brand-line bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-brand-muted">
      Engineering Calculator
    </div>
    <div className="p-6">{children}</div>
    {footer && <div className="flex justify-end border-t border-brand-line bg-white px-6 py-4">{footer}</div>}
  </div>
);

export const PolyolEqCalc = () => {
  const [oh, setOh] = useState("");
  const track = useOnceTracker("polyol-equivalent-weight");
  const res = polyolEquivalentWeight(oh);
  return (
    <Shell footer={<ResetBtn onClick={() => setOh("")} />}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-5">
          <Field label="OH Number" unit="mg KOH/g" value={oh} onChange={(v) => { setOh(v); track(); }} testid="calc-oh-input" placeholder="e.g. 56" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="Equivalent Weight" value={fmt(res)} unit="g/eq" highlight testid="calc-result-eq" />
        </div>
      </div>
    </Shell>
  );
};

export const IsoEqCalc = () => {
  const [nco, setNco] = useState("");
  const track = useOnceTracker("isocyanate-equivalent-weight");
  const res = isocyanateEquivalentWeight(nco);
  return (
    <Shell footer={<ResetBtn onClick={() => setNco("")} />}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-5">
          <Field label="NCO Content" unit="%" value={nco} onChange={(v) => { setNco(v); track(); }} testid="calc-nco-input" placeholder="e.g. 33.6" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="Equivalent Weight" value={fmt(res)} unit="g/eq" highlight testid="calc-result-eq" />
        </div>
      </div>
    </Shell>
  );
};

export const NcoOhCalc = () => {
  const [pw, setPw] = useState("");
  const [oh, setOh] = useState("");
  const [iw, setIw] = useState("");
  const [nco, setNco] = useState("");
  const track = useOnceTracker("nco-oh-ratio");
  const r = ncoOhRatio({ polyolWeight: pw, ohValue: oh, isoWeight: iw, ncoPercent: nco });
  const reset = () => { setPw(""); setOh(""); setIw(""); setNco(""); };
  return (
    <Shell footer={<ResetBtn onClick={reset} />}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Polyol Weight" unit="g" value={pw} onChange={(v) => { setPw(v); track(); }} testid="calc-polyol-weight" placeholder="100" />
          <Field label="OH Value" unit="mg KOH/g" value={oh} onChange={(v) => { setOh(v); track(); }} testid="calc-oh-value" placeholder="56" />
          <Field label="Isocyanate Weight" unit="g" value={iw} onChange={(v) => { setIw(v); track(); }} testid="calc-iso-weight" placeholder="50" />
          <Field label="NCO Content" unit="%" value={nco} onChange={(v) => { setNco(v); track(); }} testid="calc-nco-value" placeholder="33.6" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="OH Equivalent Weight" value={fmt(r?.ohEqWeight)} unit="g/eq" testid="calc-oh-eqw" />
          <ResultRow label="NCO Equivalent Weight" value={fmt(r?.ncoEqWeight)} unit="g/eq" testid="calc-nco-eqw" />
          <ResultRow label="OH Equivalents" value={fmt(r?.ohEquivalents, 4)} unit="eq" testid="calc-oh-eq" />
          <ResultRow label="NCO Equivalents" value={fmt(r?.ncoEquivalents, 4)} unit="eq" testid="calc-nco-eq" />
          <ResultRow label="NCO/OH Ratio" value={fmt(r?.ratio, 3)} unit="" highlight testid="calc-ratio" />
          <ResultRow label="Isocyanate Index" value={fmt(r?.index, 1)} unit="" highlight testid="calc-index" />
        </div>
      </div>
    </Shell>
  );
};

export const IsoIndexCalc = () => {
  const [pw, setPw] = useState("");
  const [oh, setOh] = useState("");
  const [water, setWater] = useState("");
  const [nco, setNco] = useState("");
  const [index, setIndex] = useState("");
  const track = useOnceTracker("isocyanate-index");
  const r = isocyanateRequirement({ polyolWeight: pw, ohValue: oh, waterWeight: water, ncoPercent: nco, index });
  const reset = () => { setPw(""); setOh(""); setWater(""); setNco(""); setIndex(""); };
  return (
    <Shell footer={<ResetBtn onClick={reset} />}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Polyol Weight" unit="g" value={pw} onChange={(v) => { setPw(v); track(); }} testid="calc-polyol-weight" placeholder="100" />
          <Field label="OH Value" unit="mg KOH/g" value={oh} onChange={(v) => { setOh(v); track(); }} testid="calc-oh-value" placeholder="56" />
          <Field label="Water Weight" unit="g (optional)" value={water} onChange={(v) => { setWater(v); track(); }} testid="calc-water" placeholder="3" />
          <Field label="NCO Content" unit="%" value={nco} onChange={(v) => { setNco(v); track(); }} testid="calc-nco-value" placeholder="31.5" />
          <Field label="Target Index" unit="" value={index} onChange={(v) => { setIndex(v); track(); }} testid="calc-target-index" placeholder="105" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="Polyol Equivalents" value={fmt(r?.polyolEq, 4)} unit="eq" testid="calc-polyol-eq" />
          <ResultRow label="Water Equivalents" value={fmt(r?.waterEq, 4)} unit="eq" testid="calc-water-eq" />
          <ResultRow label="Total Active H" value={fmt(r?.totalActiveH, 4)} unit="eq" testid="calc-total-h" />
          <ResultRow label="Required NCO" value={fmt(r?.requiredNcoEq, 4)} unit="eq" testid="calc-req-nco" />
          <ResultRow label="Required Isocyanate Weight" value={fmt(r?.requiredIsoWeight)} unit="g" highlight testid="calc-req-iso" />
        </div>
      </div>
    </Shell>
  );
};

export const PuMixCalc = () => {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [batch, setBatch] = useState("");
  const track = useOnceTracker("pu-mix-ratio");
  const r = puMixRatio({ partsA: a, partsB: b, batchWeight: batch });
  const reset = () => { setA(""); setB(""); setBatch(""); };
  return (
    <Shell footer={<ResetBtn onClick={reset} />}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Component A" unit="parts by wt" value={a} onChange={(v) => { setA(v); track(); }} testid="calc-parts-a" placeholder="100" />
          <Field label="Component B" unit="parts by wt" value={b} onChange={(v) => { setB(v); track(); }} testid="calc-parts-b" placeholder="50" />
          <Field label="Batch Weight" unit="g (optional)" value={batch} onChange={(v) => { setBatch(v); track(); }} testid="calc-batch-weight" placeholder="1000" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="A : B Ratio" value={r ? r.ratioText : "—"} unit="" highlight testid="calc-ratio-text" />
          <ResultRow label="Component A" value={fmt(r?.percentA)} unit="%" testid="calc-pct-a" />
          <ResultRow label="Component B" value={fmt(r?.percentB)} unit="%" testid="calc-pct-b" />
          <ResultRow label="Weight of A" value={fmt(r?.weightA)} unit="g" testid="calc-weight-a" />
          <ResultRow label="Weight of B" value={fmt(r?.weightB)} unit="g" highlight testid="calc-weight-b" />
        </div>
      </div>
    </Shell>
  );
};

export const WaterNcoCalc = () => {
  const [water, setWater] = useState("");
  const [nco, setNco] = useState("");
  const track = useOnceTracker("water-nco-demand");
  const r = waterNcoDemand({ waterWeight: water, ncoPercent: nco });
  const reset = () => { setWater(""); setNco(""); };
  return (
    <Shell footer={<ResetBtn onClick={reset} />}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Water Weight" unit="g" value={water} onChange={(v) => { setWater(v); track(); }} testid="calc-water-weight" placeholder="3" />
          <Field label="NCO Content" unit="% (optional)" value={nco} onChange={(v) => { setNco(v); track(); }} testid="calc-nco-value" placeholder="31.5" />
        </div>
        <div className="border border-brand-line bg-white">
          <ResultRow label="Water Equivalents" value={fmt(r?.waterEq, 4)} unit="eq" testid="calc-water-eq" />
          <ResultRow label="NCO Equivalents Consumed" value={fmt(r?.ncoEqConsumed, 4)} unit="eq" testid="calc-nco-consumed" />
          <ResultRow label="CO₂ Generated" value={fmt(r?.co2Moles, 4)} unit="mol" testid="calc-co2" />
          <ResultRow label="Extra Isocyanate Demand" value={fmt(r?.extraIsoWeight)} unit="g" highlight testid="calc-extra-iso" />
        </div>
      </div>
    </Shell>
  );
};

const emptyComp = () => ({ name: "", weight: "", oh: "", water: "" });

export const MultiPolyolCalc = () => {
  const [comps, setComps] = useState([{ name: "Polyol A", weight: "", oh: "", water: "" }, emptyComp()]);
  const track = useOnceTracker("multi-polyol-blend");
  const r = multiPolyolBlend(comps);
  const update = (i, k, v) => { track(); setComps((c) => c.map((row, j) => (j === i ? { ...row, [k]: v } : row))); };
  const addRow = () => setComps((c) => [...c, emptyComp()]);
  const removeRow = (i) => setComps((c) => (c.length > 1 ? c.filter((_, j) => j !== i) : c));
  const reset = () => setComps([{ name: "Polyol A", weight: "", oh: "", water: "" }, emptyComp()]);

  const cell = "h-11 w-full border border-brand-line bg-white px-2 font-mono text-sm text-brand-ink outline-none focus:ring-2 focus:ring-brand-blue";
  return (
    <Shell footer={<ResetBtn onClick={reset} />}>
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm" data-testid="multi-polyol-table">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-brand-muted">
                <th className="pb-2 pr-2">Material</th>
                <th className="pb-2 pr-2">Weight (g)</th>
                <th className="pb-2 pr-2">OH No.</th>
                <th className="pb-2 pr-2">Water (%)</th>
                <th className="pb-2 pr-2 text-right">Equiv.</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {comps.map((row, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2"><input className={cell} value={row.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`calc-comp-name-${i}`} placeholder="Material" /></td>
                  <td className="py-1 pr-2"><input type="number" inputMode="decimal" className={cell} value={row.weight} onChange={(e) => update(i, "weight", e.target.value)} data-testid={`calc-comp-weight-${i}`} placeholder="60" /></td>
                  <td className="py-1 pr-2"><input type="number" inputMode="decimal" className={cell} value={row.oh} onChange={(e) => update(i, "oh", e.target.value)} data-testid={`calc-comp-oh-${i}`} placeholder="56" /></td>
                  <td className="py-1 pr-2"><input type="number" inputMode="decimal" className={cell} value={row.water} onChange={(e) => update(i, "water", e.target.value)} data-testid={`calc-comp-water-${i}`} placeholder="0" /></td>
                  <td className="py-1 pr-2 text-right font-mono text-brand-blue">{r && r.rows[i] ? fmt(r.rows[i].eq, 4) : "—"}</td>
                  <td className="py-1 text-right">
                    <button onClick={() => removeRow(i)} data-testid={`calc-remove-row-${i}`} className="px-2 text-brand-muted hover:text-brand-red" aria-label="Remove">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addRow} data-testid="calc-add-row" className="inline-flex items-center gap-1.5 border border-brand-line px-4 py-2 text-sm font-medium text-brand-blue transition-colors hover:border-brand-blue">
          + Add component
        </button>

        <div className="grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2">
          <ResultRow label="Total Weight" value={fmt(r?.totalWeight)} unit="g" testid="calc-total-weight" />
          <ResultRow label="Total Equivalents" value={fmt(r?.totalEq, 4)} unit="eq" testid="calc-total-eq" />
          <ResultRow label="Blended Equivalent Weight" value={fmt(r?.blendedEqWeight)} unit="g/eq" highlight testid="calc-blended-eqw" />
          <ResultRow label="Blended OH Number" value={fmt(r?.blendedOh)} unit="mg KOH/g" highlight testid="calc-blended-oh" />
        </div>
      </div>
    </Shell>
  );
};

export const CALC_WIDGETS = {
  "polyol-equivalent-weight": PolyolEqCalc,
  "isocyanate-equivalent-weight": IsoEqCalc,
  "nco-oh-ratio": NcoOhCalc,
  "isocyanate-index": IsoIndexCalc,
  "pu-mix-ratio": PuMixCalc,
  "water-nco-demand": WaterNcoCalc,
  "multi-polyol-blend": MultiPolyolCalc,
};
